// PrefabWizard — multi-section form. A prefab is a DB record that bundles a
// body animation + audio + (optional) face animation. Not a file upload;
// launched from the "+ New Prefab" button in the ContentBrowser toolbar.
//
// Sourced from assetRegistry so the dropdowns automatically pick up assets
// the user just uploaded.

import React, { useMemo, useState } from 'react'
import WizardShell, { inputStyle, labelStyle, tokens } from './WizardShell'
import { CustomAssetManager } from '../../../lib/CustomAssetManager'
import { assetRegistry } from '../../../lib/assetRegistry'
import { reflectPrefabUpsert } from '../../../lib/prefabReflection'
import PrefabTriggeringSection, { PrefabSectionHeader } from '../../PrefabTriggeringSection'
import FilterSelect from './FilterSelect'

function slugify(s) {
  return (s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
}

export default function PrefabWizard({ path = [], onDone, onCancel }) {
  const [, force] = useState(0)
  React.useEffect(() => assetRegistry.subscribe(() => force(n => n + 1)), [])

  const records = assetRegistry.list()
  const bodyOptions = useMemo(() => records.filter(r => r.kind === 'body_anim'), [records.length])
  const faceOptions = useMemo(() => records.filter(r => r.kind === 'face_anim'), [records.length])
  const audioOptions = useMemo(() => records.filter(r => r.kind === 'audio'), [records.length])

  const [displayName, setDisplayName] = useState('')
  const [tag, setTag] = useState('')
  const [bodyAnimationId, setBodyAnimationId] = useState(null)
  const [faceAnimationId, setFaceAnimationId] = useState(null)
  const [audioAssetId, setAudioAssetId] = useState(null)
  const [durationMs, setDurationMs] = useState(3000)
  const [blendInMs, setBlendInMs] = useState(180)
  const [blendOutMs, setBlendOutMs] = useState(220)
  const [loop, setLoop] = useState(false)
  // Triggering half (ENG-843) — convorcher-bound, saved via prefabReflection.
  const [phrases, setPhrases] = useState([])
  const [notFor, setNotFor] = useState([])
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  // Set when the AAM create succeeded but the convorcher trigger sync failed —
  // the next submit retries ONLY the sync (re-creating the AAM row would 409).
  const [savedRow, setSavedRow] = useState(null)

  // When the user picks an audio asset, default duration to its length.
  const onAudioPick = id => {
    setAudioAssetId(id)
    const a = audioOptions.find(o => o.rawId === id)
    if (a?.durationMs) setDurationMs(a.durationMs)
  }

  // Auto-fill tag from displayName when tag is still empty / auto-derived.
  const onNameChange = v => {
    const auto = slugify(displayName)
    setDisplayName(v)
    if (!tag || tag === auto) setTag(slugify(v))
  }

  const canSubmit = displayName.trim() && tag.trim() && (bodyAnimationId || audioAssetId)

  const submit = async () => {
    if (!canSubmit) { setError('Need at least a name, tag, and one of body anim or audio.'); return }
    setSubmitting(true); setError(null)
    try {
      if (savedRow) {
        // AAM row already exists from the failed attempt — retry the
        // convorcher trigger sync only.
        await reflectPrefabUpsert({ slug: CustomAssetManager.getCharacterId(), tag: savedRow.tag, text: savedRow.text, phrases, notFor })
        onDone?.(savedRow)
        return
      }
      const row = await CustomAssetManager.createPrefab({
        tag: tag.trim(),
        displayName: displayName.trim(),
        bodyAnimationId,
        faceAnimationId,
        audioAssetId,
        durationMs,
        blendInMs,
        blendOutMs,
        loop,
        status: 'ready',
        phrases,
        notFor,
      })
      assetRegistry.setPath(`prefab:${row.id}`, path)
      onDone?.(row)
    } catch (e) {
      if (e.reflectionOnly && e.row) {
        assetRegistry.setPath(`prefab:${e.row.id}`, path)
        setSavedRow(e.row)
      }
      setError(String(e.message ?? e))
      setSubmitting(false)
    }
  }

  const preview = () => {
    // Fire the same playPrefab path the EventRuntime uses, with the current
    // form values. Lets the artist hear/see the combination before saving.
    const body = bodyOptions.find(o => o.rawId === bodyAnimationId)
    const face = faceOptions.find(o => o.rawId === faceAnimationId)
    const audio = audioOptions.find(o => o.rawId === audioAssetId)
    const bodyClipName = body ? `custom_body_${body.rawId}` : null
    if (bodyClipName && window.__DIRECT_PLAY__ !== undefined) {
      const now = Date.now()
      window.__DIRECT_PLAY__ = {
        clipName: bodyClipName,
        startTime: now,
        expiresAt: now + (durationMs || 3000),
        blendInMs, blendOutMs,
      }
    }
    if (face) window.__FACE_CLIP_PLAYER__?.manualPlay?.(`custom_face_${face.rawId}`, 0.8, durationMs)
    if (audio?.url) new Audio(audio.url).play().catch(() => {})
  }

  return (
    <WizardShell
      title="New prefab"
      subtitle="Bundle a body animation, audio, and optional face into a tagged response."
      onCancel={onCancel}
      onSubmit={submit}
      submitDisabled={!canSubmit || submitting}
      submitLabel={submitting ? 'Saving…' : savedRow ? 'Retry trigger sync' : 'Save prefab'}
      error={error}
    >
      <PrefabSectionHeader first name="Playback" note={{ prefix: 'stored in', target: 'AAM' }} />

      <label style={labelStyle}>Display name</label>
      <input style={inputStyle} value={displayName} onChange={e => onNameChange(e.target.value)} placeholder="e.g. Back To Work" autoFocus />

      <label style={labelStyle}>Tag</label>
      <input style={inputStyle} value={tag} onChange={e => setTag(slugify(e.target.value))} placeholder="e.g. back_to_work" />
      <div style={{ fontFamily: 'var(--m-font)', fontSize: 9.5, color: tokens.dim, marginTop: 4 }}>
        Fire from blueprint with <code>onPrefabTrigger</code> · "{tag}"
      </div>

      <label style={labelStyle}>Body animation</label>
      <FilterSelect value={bodyAnimationId} onChange={setBodyAnimationId} options={bodyOptions} />

      <label style={labelStyle}>Audio</label>
      <FilterSelect value={audioAssetId} onChange={onAudioPick} options={audioOptions} />

      <label style={labelStyle}>Face animation (optional)</label>
      <FilterSelect value={faceAnimationId} onChange={setFaceAnimationId} options={faceOptions} />

      <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
        <NumField label="Duration (ms)" value={durationMs} onChange={setDurationMs} />
        <NumField label="Blend in (ms)" value={blendInMs} onChange={setBlendInMs} />
        <NumField label="Blend out (ms)" value={blendOutMs} onChange={setBlendOutMs} />
      </div>

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 12, color: tokens.text, fontFamily: 'var(--m-font)', fontSize: 11, cursor: 'pointer' }}>
        <input type="checkbox" checked={loop} onChange={e => setLoop(e.target.checked)} /> Loop body animation
      </label>

      <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
        <button onClick={preview} style={previewBtn}>▶ Preview</button>
      </div>

      <PrefabSectionHeader name="Triggering" note={{ prefix: 'synced to', target: 'convorcher' }} />
      <PrefabTriggeringSection
        tag={tag}
        phrases={phrases}
        notFor={notFor}
        onPhrasesChange={setPhrases}
        onNotForChange={setNotFor}
      />
    </WizardShell>
  )
}

function NumField({ label, value, onChange }) {
  return (
    <div style={{ flex: 1 }}>
      <label style={{ ...labelStyle, marginTop: 4 }}>{label}</label>
      <input
        type="number"
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={inputStyle}
      />
    </div>
  )
}

const previewBtn = {
  background: 'transparent',
  border: `1px solid ${tokens.border}`,
  color: tokens.dim,
  borderRadius: 4,
  padding: '5px 12px',
  fontFamily: 'var(--m-font)', fontSize: 11,
  letterSpacing: '.06em', textTransform: 'uppercase', fontWeight: 600,
  cursor: 'pointer',
}
